import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import { makeStyles } from '@material-ui/core/styles';
import { adsErrorMessage, getMetaAdsLeadDetails, isCanceled } from '../../services/metaAdsService';
import { escapeCsv, formatMoney } from './format';

const useStyles = makeStyles(() => ({
    paper: {
        minWidth: 860,
        maxWidth: '96vw',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: 8,
        gap: 12,
    },
    titleActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
    },
    downloadBtn: {
        backgroundColor: '#1C375D',
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#152a47',
        },
        '&:disabled': {
            backgroundColor: '#c5cad3',
            color: '#fff',
        },
    },
    dialogContent: {
        overflow: 'hidden',
        paddingTop: 8,
    },
    tableWrap: {
        maxHeight: 'min(65vh, calc(100vh - 220px))',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        fontSize: 13,
    },
    th: {
        textAlign: 'left',
        padding: '10px 12px',
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
        fontSize: 11,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        boxShadow: '0 2px 4px rgba(16, 24, 40, 0.12)',
    },
    td: {
        padding: '10px 12px',
        borderBottom: '1px solid #eef0f3',
        verticalAlign: 'top',
    },
    empty: {
        padding: 24,
        color: '#8a92a3',
        textAlign: 'center',
    },
    error: {
        padding: 16,
        color: '#a61b1b',
    },
    leadLink: {
        color: '#1C375D',
        fontWeight: 600,
        textDecoration: 'underline',
        '&:hover': {
            color: '#F47E44',
        },
    },
}));

function erpLeadLogsPath(row) {
    if (!row?.erpLeadId || row.currentStage === 'Not in ERP yet') return null;
    return `/project-logs/${row.erpLeadId}`;
}

function LeadRefCell({ classes, value, row }) {
    const path = erpLeadLogsPath(row);
    const label = value ?? '—';
    if (!path || label === '—') {
        return <span>{label}</span>;
    }
    return (
        <a
            href={path}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.leadLink}
            onClick={(event) => event.stopPropagation()}
        >
            {label}
        </a>
    );
}

function formatLeadDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function csvLeadDate(value) {
    const formatted = formatLeadDate(value);
    return formatted === '—' ? '' : formatted;
}

function fileSlugFromTitle(title) {
    return String(title || 'meta-ads-leads')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'meta-ads-leads';
}

export function downloadLeadRowsCsv(title, rows, createdDateLabel = 'Meta created date', showContact = false) {
    const header = [
        'S.No.',
        'Unique lead no.',
        'Lead no.',
        'Source',
        'Campaign',
        'Ad set',
        'Ad',
        ...(showContact ? ['Customer', 'Email'] : []),
        createdDateLabel,
        'Lead assigned date',
        'Current stage',
        'Real type',
        'Sales Won value',
        'Sales Won date',
        'Assigned to',
        'ERP lead id',
    ];
    const lines = [header.map(escapeCsv).join(',')];
    rows.forEach((row) => {
        lines.push([
            row.sn,
            row.uniqueLeadNumber || '',
            row.leadNo ?? '',
            row.source || '',
            row.campaignName || '',
            row.adsetName || '',
            row.adName || '',
            ...(showContact ? [row.customerName || '', row.email || ''] : []),
            csvLeadDate(row.metaCreatedDate ?? row.createdDate),
            csvLeadDate(row.leadAssignedDate),
            row.currentStage || '',
            row.realType || '',
            row.staticSalesWonAmount ?? '',
            csvLeadDate(row.leadWonDate),
            row.assignTo || '',
            row.erpLeadId || '',
        ].map(escapeCsv).join(','));
    });
    const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileSlugFromTitle(title)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
}

export default function MetaAdsLeadDialog({
    open,
    title,
    query,
    onClose,
    loadLeads = getMetaAdsLeadDetails,
    createdDateLabel = 'Meta created date',
    showContact = false,
}) {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [rows, setRows] = useState([]);
    useEffect(() => {
        if (!open || !query) return undefined;
        const controller = new AbortController();
        setLoading(true);
        setError('');
        setRows([]);
        loadLeads({ ...query, signal: controller.signal })
            .then((data) => {
                if (controller.signal.aborted) return;
                setRows(Array.isArray(data?.leads) ? data.leads : []);
            })
            .catch((loadError) => {
                if (isCanceled(loadError) || controller.signal.aborted) return;
                setError(adsErrorMessage(loadError, 'Unable to load leads.'));
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [open, query, loadLeads]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" classes={{ paper: classes.paper }}>
            <DialogTitle disableTypography>
                <div className={classes.titleRow}>
                    <h2 style={{ margin: 0, fontSize: 18, color: '#1C375D', flex: 1 }}>{title}</h2>
                    <div className={classes.titleActions}>
                        <Button
                            className={classes.downloadBtn}
                            variant="contained"
                            size="small"
                            startIcon={<GetAppIcon />}
                            disabled={loading || !rows.length}
                            onClick={() => downloadLeadRowsCsv(title, rows, createdDateLabel, showContact)}
                        >
                            Download CSV
                        </Button>
                        <IconButton aria-label="Close" onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>
            </DialogTitle>
            <DialogContent dividers className={classes.dialogContent}>
                {loading && (
                    <div className={classes.empty}>
                        <CircularProgress size={28} />
                    </div>
                )}
                {!loading && error && (
                    <p className={classes.error} role="alert">{error}</p>
                )}
                {!loading && !error && rows.length === 0 && (
                    <p className={classes.empty}>No leads found for this selection.</p>
                )}
                {!loading && !error && rows.length > 0 && (
                    <div className={classes.tableWrap}>
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th className={classes.th}>S.No.</th>
                                    <th className={classes.th}>Unique lead no.</th>
                                    <th className={classes.th}>Lead no.</th>
                                    <th className={classes.th}>Source</th>
                                    <th className={classes.th}>Campaign</th>
                                    {showContact && <th className={classes.th}>Customer</th>}
                                    {showContact && <th className={classes.th}>Email</th>}
                                    <th className={classes.th}>{createdDateLabel}</th>
                                    <th className={classes.th}>Lead assigned date</th>
                                    <th className={classes.th}>Current stage</th>
                                    <th className={classes.th}>Real type</th>
                                    <th className={classes.th}>Sales Won value</th>
                                    <th className={classes.th}>Sales Won date</th>
                                    <th className={classes.th}>Assigned to</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={`${row.erpLeadId}-${row.sn}`}>
                                        <td className={classes.td}>{row.sn}</td>
                                        <td className={classes.td}>
                                            <LeadRefCell
                                                classes={classes}
                                                value={row.uniqueLeadNumber || '—'}
                                                row={row}
                                            />
                                        </td>
                                        <td className={classes.td}>
                                            <LeadRefCell
                                                classes={classes}
                                                value={row.leadNo ?? '—'}
                                                row={row}
                                            />
                                        </td>
                                        <td className={classes.td}>{row.source || '—'}</td>
                                        <td className={classes.td}>{row.campaignName || '—'}</td>
                                        {showContact && <td className={classes.td}>{row.customerName || '—'}</td>}
                                        {showContact && <td className={classes.td}>{row.email || '—'}</td>}
                                        <td className={classes.td}>
                                            {formatLeadDate(row.metaCreatedDate ?? row.createdDate)}
                                        </td>
                                        <td className={classes.td}>{formatLeadDate(row.leadAssignedDate)}</td>
                                        <td className={classes.td}>{row.currentStage || '—'}</td>
                                        <td className={classes.td}>{row.realType || '—'}</td>
                                        <td className={classes.td}>
                                            {row.staticSalesWonAmount != null && row.staticSalesWonAmount !== ''
                                                ? formatMoney(row.staticSalesWonAmount, 'INR')
                                                : '—'}
                                        </td>
                                        <td className={classes.td}>{formatLeadDate(row.leadWonDate)}</td>
                                        <td className={classes.td}>{row.assignTo || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
